(ns lupapalvelu.ui.topbar-connected
  "Connects the pure `lupapalvelu.ui2.topbar/topbar` to live Lupapiste app state."
  (:require [lupapalvelu.dev]
            [lupapalvelu.next.event :refer [<sub]]
            [lupapalvelu.next.session :as sess]
            [lupapalvelu.ui.common :refer [feature? loc]]
            [lupapalvelu.ui2.icons :as icons]
            [lupapalvelu.ui2.topbar :refer [topbar]]
            [re-frame.core :as rf]
            [reagent.core :refer [defc]]))

(defn- purpose->user-item
  "Converts a usage-purpose map to a topbar user-item with the correct label, icon and link.
  Mirrors the KO UserMenu purposeName/purposeIcon/purposeLink functions."
  [lang org-names {:keys [type orgId]}]
  (if (= type "authority-admin")
    (let [org-name (when (and org-names orgId)
                     (get-in org-names [(keyword orgId) (keyword lang)]))]
      {:key   (str "authority-admin-" orgId)
       :label (str (loc "authorityAdmin.settings") (when org-name (str " – " org-name)))
       :icon  icons/settings
       :href  (str "/app/" lang "/authority-admin/" orgId)})
    {:key   (str "purpose-" type)
     :label (loc "permit.service")
     :icon  icons/home
     :href  (str "/app/" lang "/" type)}))

(rf/reg-sub ::applications-app
  :<- [:auth/global? :applications-search]
  (fn [allowed? _]
    (when allowed?
      {:key   "dashboard-applications"
       :label (loc "topbar.dashboard.hankelista")
       :icon  icons/archive
       :href  "#!/applications"})))

(rf/reg-sub ::workspace-app
  :<- [::sess/beta-user?]
  (fn [beta-user? _]
    (when (and beta-user? (feature? :dashboard-view))
      {:key   "dashboard-workspace"
       :label (loc "topbar.dashboard.tyopoyta-beta")
       :icon  icons/archive
       :href  "#!/dashboard"})))

(rf/reg-sub ::document-search-app
  :<- [:auth/global? :document-search-access-allowed]
  (fn [allowed? _]
    {:key       "document-search"
     :label     (loc "topbar.document-search")
     :icon      icons/archive
     :href      (not-empty (loc "document.search.url"))
     :disabled? (not allowed?)}))

(rf/reg-sub ::rami-app
  :<- [:auth/global? :rami-access]
  (fn [allowed? _]
    {:key       "rami"
     :label     (loc "reporting")
     :icon      icons/document-multiple
     :href      "https://rami.lupapiste.fi/"
     :disabled? (not allowed?)}))

(rf/reg-sub ::billing-app
  :<- [:auth/global? :biller-organizations]
  (fn [allowed? _]
    {:key       "billing"
     :label     (loc "billing")
     :icon      icons/shopping-cart
     :href      "#!/biller"
     :disabled? (not allowed?)}))

(rf/reg-sub ::toj-app
  :<- [:auth/global? :toj-access-allowed]
  (fn [allowed? _]
    {:key       "toj"
     :label     (loc "topbar.toj")
     :icon      icons/tree-view
     :href      (not-empty (loc "toj.url"))
     :disabled? (not allowed?)}))

(rf/reg-sub ::luparekisteri-app
  (fn [_ _]
    {:key       "luparekisteri"
     :label     (loc "topbar.luparekisteri")
     :icon      icons/archive
     :disabled? true}))

(rf/reg-sub ::apps
  :<- [::applications-app]
  :<- [::workspace-app]
  :<- [::document-search-app]
  :<- [::rami-app]
  :<- [::billing-app]
  :<- [::toj-app]
  :<- [::luparekisteri-app]
  (fn [apps _]
    (vec (remove nil? apps))))

(rf/reg-sub ::language-menu
  (fn [_ _]
    (let [current-language (js/loc.getCurrentLanguage)]
      {:language current-language
       :current  (some-> current-language .toUpperCase)
       :items    (mapv (fn [language]
                         {:key      language
                          :label    (.toUpperCase language)
                          :on-click (fn [_]
                                      (when (not= language current-language)
                                        (js/hub.send "change-lang"
                                                     (clj->js {:lang language}))))})
                       (array-seq (js/loc.getSupportedLanguages)))})))

(rf/reg-sub ::user-items
  :<- [::sess/language]
  :<- [::sess/alt-usage-purposes]
  :<- [::sess/page-authority-admin?]
  :<- [::sess/org-names]
  (fn [[lang alts authority-admin? org-names] _]
    (cond-> [{:key   "mypage"
              :label (loc "topbar.mypage")
              :icon  icons/user
              :href  "#!/mypage"}]
      (seq alts)       (into (map (partial purpose->user-item lang org-names) alts))
      authority-admin? (conj {:key   "admin-settings"
                              :label (loc "authorityAdmin.settings")
                              :icon  icons/settings
                              :href  (str "/app/" lang "/authority-admin")}))))

(defn- open-start-page
  "openStartPage() from `/resources/private/common/app.js` in cljs"
  [event]
  (.preventDefault event)
  ;; Keep the same precedence as the legacy app.js logo handler: explicit logo
  ;; path first, then configured SPA start page, otherwise current app root.
  (let [location   (.-location js/window)
        logo-path  (.-logoPath js/lupapisteApp)
        start-page (.-startPage js/lupapisteApp)]
    (cond
      logo-path
      (set! (.-href location)
            (str (.-protocol location) "//" (.-host location) logo-path))

      (and start-page (not= "/" (.charAt start-page 0)))
      (.openPage js/pageutil start-page)

      :else
      (set! (.-href location)
            (str (.-origin location) (.-pathname location) (.-search location))))))

(defc connected-topbar []
  (let [language-menu (<sub [::language-menu])]
    [topbar
     {:on-logo-click  open-start-page
      :language       (:current language-menu)
      :language-items (:items language-menu)
      :user           {:name     (<sub [::sess/display-name])
                       :org-name (or (<sub [::sess/current-org-name])
                                     (when-let [role (<sub [::sess/role])]
                                       (loc (case role "authority-admin" "authorityAdmin" role))))}
      :apps-label     (loc "topbar.apps.title")
      :apps-subtitle  (loc "topbar.apps.subtitle")
      :apps           (<sub [::apps])
      :user-items     (<sub [::user-items])
      :on-logout      (fn [_] (set! (.-location js/window) (str "/app/" (:language language-menu) "/logout")))}]))
